import { ADDRESSES, ZERO_ADDR } from '@/lib/contracts'
import poolAbi from '@/lib/abis/HorizonLendingPool.json'
import { getLogsChunked } from '@/lib/getLogsChunked'

const ev = (n) => poolAbi.find((x) => x.type === 'event' && x.name === n)
const TRANSFER = {
  type: 'event',
  name: 'Transfer',
  inputs: [
    { name: 'from', type: 'address', indexed: true },
    { name: 'to', type: 'address', indexed: true },
    { name: 'value', type: 'uint256', indexed: false },
  ],
}

/// Reconstruct a wallet's WETH (native-ETH) supply/withdraw leg, which the pool's own Supplied/
/// Withdrawn events DON'T attribute to it.
///
/// Native ETH supplies/withdrawals route through `WETHGateway`, which calls `pool.supply(weth, amount)`
/// as ITSELF and then transfers the receipt hToken to the wallet (`supplyETH` -> `safeTransfer`). So the
/// pool credits the GATEWAY (`msg.sender`) in `Supplied`/`Withdrawn`, not the wallet - a scan filtering
/// those events by `user: wallet` misses every ETH supply, while borrow (which uses `onBehalfOf`) is
/// attributed correctly. That asymmetry is why the portfolio history charted only debt (going negative)
/// and the per-market activity list showed borrows only.
///
/// This joins the gateway's own Supplied/Withdrawn events to the receipt-token (`S_TOKEN_WETH`)
/// transfers that moved the position to/from THIS wallet in the SAME transaction - so each event is
/// attributed to the right wallet and carries the EXACT underlying ETH `amount` the pool logged (not a
/// share balance that drifts from ETH as the supply index grows). Direct (non-gateway) WETH supplies
/// still surface through the normal `Supplied{user}` scan and are NOT re-emitted here (their receipt is
/// minted from the zero address, not transferred from the gateway), so the two sources never double-count.
///
/// Returns `[]` when the gateway or receipt token is not configured for this chain (i.e. WETH is
/// supplied directly and the normal scan already attributes it).
export async function fetchGatewayWethActivity(client, user, fromBlock) {
  const gateway = ADDRESSES.WETH_GATEWAY
  const sToken = ADDRESSES.S_TOKEN_WETH
  const weth = ADDRESSES.WETH
  if (!gateway || gateway === ZERO_ADDR || !sToken || sToken === ZERO_ADDR || !weth) return []

  const [supplied, withdrawn, receiptIn, receiptOut] = await Promise.all([
    getLogsChunked(client, { address: ADDRESSES.LENDING_POOL, event: ev('Supplied'), args: { asset: weth, user: gateway }, fromBlock }),
    getLogsChunked(client, { address: ADDRESSES.LENDING_POOL, event: ev('Withdrawn'), args: { asset: weth, user: gateway }, fromBlock }),
    getLogsChunked(client, { address: sToken, event: TRANSFER, args: { from: gateway, to: user }, fromBlock }),
    getLogsChunked(client, { address: sToken, event: TRANSFER, args: { from: user, to: gateway }, fromBlock }),
  ])
  // A receipt transfer to (from) the wallet in the same tx as a gateway supply (withdraw) is THIS
  // wallet's action; a gateway supply whose tx has no such transfer belongs to a different wallet.
  const inTx = new Set(receiptIn.map((l) => l.transactionHash))
  const outTx = new Set(receiptOut.map((l) => l.transactionHash))

  const out = []
  for (const l of supplied) {
    if (inTx.has(l.transactionHash)) {
      out.push({ kind: 'Supplied', sign: +1, asset: weth, amount: l.args.amount, blockNumber: l.blockNumber, logIndex: l.logIndex, txHash: l.transactionHash })
    }
  }
  for (const l of withdrawn) {
    if (outTx.has(l.transactionHash)) {
      out.push({ kind: 'Withdrawn', sign: -1, asset: weth, amount: l.args.amount, blockNumber: l.blockNumber, logIndex: l.logIndex, txHash: l.transactionHash })
    }
  }
  return out
}
